/**
 * Orden personalizado de ítems del sidebar admin — persistencia local por
 * usuario Clerk (no sync entre dispositivos). Patrón portado de clarimoine-fan
 * (dashboardSidebarOrderStorage), adaptado al sidebar agrupado nuestro:
 * el orden se guarda POR GRUPO (los grupos son ejes semánticos y no se mezclan).
 */

import type { AdminSidebarNavItem } from "@/components/admin/adminSidebarNav";

const STORAGE_KEY_PREFIX = "juaperrone:admin-sidebar-order:v1";

export function sidebarOrderStorageKey(clerkUserId: string): string {
  return `${STORAGE_KEY_PREFIX}:${clerkUserId}`;
}

/** { [groupLabel]: pathsEnOrden[] } */
export type SidebarOrderMap = Record<string, string[]>;

export function loadSidebarOrder(clerkUserId: string | undefined | null): SidebarOrderMap | null {
  if (!clerkUserId || typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(sidebarOrderStorageKey(clerkUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const map: SidebarOrderMap = {};
    for (const [group, order] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(order)) {
        map[group] = order.filter((x): x is string => typeof x === "string" && x.length > 0);
      }
    }
    return map;
  } catch {
    return null;
  }
}

export function saveSidebarOrder(clerkUserId: string, order: SidebarOrderMap): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(sidebarOrderStorageKey(clerkUserId), JSON.stringify(order));
  } catch {
    // storage lleno/bloqueado — el reorden simplemente no persiste
  }
}

export function clearSidebarOrder(clerkUserId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(sidebarOrderStorageKey(clerkUserId));
  } catch {
    // ignorar
  }
}

/**
 * Aplica el orden guardado a los ítems de un grupo. Ítems nuevos (no
 * presentes en el orden guardado) van al final en su orden declarado —
 * así un deploy con módulos nuevos nunca los esconde.
 */
export function sortGroupItems(
  groupLabel: string,
  items: readonly AdminSidebarNavItem[],
  saved: SidebarOrderMap | null,
): readonly AdminSidebarNavItem[] {
  const order = saved?.[groupLabel];
  if (!order?.length) return items;
  const byPath = new Map(items.map((i) => [i.to, i] as const));
  const ordered: AdminSidebarNavItem[] = [];
  const seen = new Set<string>();
  for (const path of order) {
    const item = byPath.get(path);
    if (item) {
      ordered.push(item);
      seen.add(path);
    }
  }
  for (const item of items) {
    if (!seen.has(item.to)) ordered.push(item);
  }
  return ordered;
}
